import React from 'react';
import { RotatingLoader } from 'components/atoms/Base/Logo/RotatingLoader';

interface LoadingIndicatorProps {
  type?: 'dots' | 'pulse' | 'bounce' | 'rotating';
  color?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';
}

const LoadingIndicator: React.FC<LoadingIndicatorProps> = ({ 
  type = 'rotating', 
  color = 'primary', 
  size = 'md' 
}) => {
  // Configure sizes for rotating loader
  const rotatingSizes = {
    sm: 20,
    md: 40,
    lg: 50,
    xl: 70,
    '2xl': 80,
    '3xl': 100
  };

  // Configure sizes for other loaders
  const sizes = {
    sm: {
      container: 'h-4',
      dot: 'w-1 h-1',
      spacing: 'space-x-1'
    },
    md: {
      container: 'h-6',
      dot: 'w-3 h-3',
      spacing: 'space-x-1.5'
    },
    lg: {
      container: 'h-8',
      dot: 'w-2.5 h-2.5',
      spacing: 'space-x-2'
    },
    xl: {
      container: 'h-10',
      dot: 'w-4 h-4',
      spacing: 'space-x-3'
    },
    '2xl': {
      container: 'h-12',
      dot: 'w-5 h-5',
      spacing: 'space-x-4'
    },
    '3xl': {
      container: 'h-16',
      dot: 'w-6 h-6',
      spacing: 'space-x-5'
    }
  };

  // Configure colors
  const colors = {
    primary: 'bg-[#00b3ff]',
    secondary: 'bg-[#b0cb1f]',
    success: 'bg-[#ffed00]',
    danger: 'bg-red-600',
    white: 'bg-white',
    black: 'bg-black'
  };

  const colorClass = colors[color as keyof typeof colors] || colors.primary;
  const sizeObj = sizes[size];
  
  if (type === 'rotating') {
    return (
      <RotatingLoader 
        size={rotatingSizes[size as keyof typeof rotatingSizes]} 
        speed={200}
        className="flex items-center justify-center"
      />
    );
  }
  

  if (type === 'dots') {
    return (
      <div className={`flex items-center ${sizeObj.spacing} ${sizeObj.container}`}>
        <div className={`${sizeObj.dot}  bg-[#00b3ff] rounded-full animate-bounce`} 
             style={{ animationDelay: '0ms' }}></div>
        <div className={`${sizeObj.dot} bg-[#b0cb1f] rounded-full animate-bounce`} 
             style={{ animationDelay: '150ms' }}></div>
        <div className={`${sizeObj.dot} bg-[#ffed00] rounded-full animate-bounce`} 
             style={{ animationDelay: '300ms' }}></div>
      </div>
    );
  }
  

  if (type === 'pulse') {
    return (
      <div className={`flex items-center justify-center ${sizeObj.container}`}>
        <div className={`${sizeObj.dot} ${colorClass} rounded-full animate-ping opacity-75`}></div>
      </div>
    );
  }
  

  if (type === 'bounce') {
    return (
      <div className="flex items-center justify-center space-x-1">
        <div className="sk-bounce-dot">
          <div className={`${sizeObj.dot} ${colorClass} rounded-full sk-bounce-1`}></div>
        </div>
        <div className="sk-bounce-dot">
          <div className={`${sizeObj.dot} ${colorClass} rounded-full sk-bounce-2`}></div>
        </div>
      </div>
    );
  }
  
  // Default fallback
  return (
    <div className="flex items-center justify-center">
      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2"></div>
    </div>
  );
};

export default LoadingIndicator;